// @ts-strict-ignore
/**
 * @fileoverview SelectField is a wrapper for beacon select components
 * to play well with react-hook-form
 * @important This is primarily different from SelectController because it doesn't
 * require passing render and allows passing any select option. The code to find the
 * option is copied from SelectController
 */
import React from "react";
import type { FieldValues } from "react-hook-form";
import { useController } from "react-hook-form";

import getUuid from "uuid-by-string";

import type { GroupBase } from "@braze/beacon-lab-select";
import { Select } from "@braze/beacon-lab-select";

import { stableValueHash } from "@services/useDashboardApi";

import { optionValueForSelect } from "../SelectController/utils";
import type { SelectFieldProps } from "./types";

export const SelectField = <
  OptionType,
  IsMulti extends boolean,
  GroupType extends GroupBase<OptionType>,
  FormValues extends FieldValues = FieldValues,
>(
  props: SelectFieldProps<OptionType, IsMulti, GroupType, FormValues>
): React.ReactElement => {
  const { control, name, defaultValue, rules, Component = Select, ...selectProps } = props;
  const { options, isMulti } = selectProps;
  const {
    field: { onChange, ...field },
    fieldState: { error },
  } = useController({ control, name, defaultValue, rules });

  /**
   * The rest of this code is to handle react-select's interations
   * by default react-select uses options to compare values
   * This means we need to find the matching option from the options array
   * based on the value from the form
   *  */
  const isEqualValue = (value: any, other: any) => {
    if (typeof value === "object" && other) {
      return getUuid(stableValueHash(value)) === getUuid(stableValueHash(other));
    }
    return value === other;
  };

  let value;
  if (options) {
    /**
     * react-select also has the ability to nest options in groups, so we
     * flatten that options map to find the correct value
     */
    let flatOptions;
    if ((options[0] as GroupType)?.options) {
      flatOptions = (options as GroupType[]).reduce((prev, curr) => prev.concat(curr.options), []);
    } else {
      flatOptions = options;
    }
    value = isMulti
      ? flatOptions.filter((o) => field.value?.includes(o.value))
      : flatOptions.find(({ value }) => isEqualValue(value, field.value));
  } else {
    value = isMulti ? field.value?.map((val) => optionValueForSelect(val)) : optionValueForSelect(field.value);
  }

  const handleChange = (e) => {
    if (isMulti) {
      return onChange(e.map((o) => o.value) ?? []);
    }

    onChange(e?.value ?? null);
  };

  // Selects don't clear the value when it's undefined
  if (value === undefined) {
    value = null;
  }

  return <Component {...field} {...selectProps} value={value} onChange={handleChange} invalid={!!error} />;
};
